import { AutoRecipeDefaultsLoadError } from "components/autoRecipeParameters/AutoRecipeDefaultsLoadError";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import dayjs from "dayjs";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useState } from "react";
import { useTenantTranslation } from "hooks/formatters";

import { useTenant } from "hooks/settings";
import { useNavigateToNewAutoSearch } from "hooks/navigation";
import {
  SearchType,
  useGetAutoRecipeDefaultsQuery,
} from "store/api/generatedApi";
import { searchTypeLabels } from "components/autoRecipeParameters/searchTypeLabels";

type MonthChoice = "this" | "next";

export const PurchasingAutoRecipeMonthDialog = ({
  searchType,
  close,
}: {
  searchType: SearchType;
  close: () => void;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const defaults = useGetAutoRecipeDefaultsQuery(
    { tenantName, searchType, scope: "search" },
    { refetchOnMountOrArgChange: true }
  );
  const [create, { isLoading }] = useNavigateToNewAutoSearch();
  const [error, setError] = useState(false);
  const [choice, setChoice] = useState<MonthChoice>("next");
  const { localTimezone, toLocalDatetime } = useLocalDatetime();
  const [now] = useState(() => dayjs().tz(localTimezone));

  const thisMonthName = toLocalDatetime(now.toISOString(), "MMMM YYYY");
  const nextMonthName = toLocalDatetime(
    now.add(1, "month").toISOString(),
    "MMMM YYYY"
  );

  return (
    <Dialog
      open
      onClose={() => {
        if (!isLoading) {
          close();
        }
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{t(searchTypeLabels[searchType])}</DialogTitle>

      <DialogContent>
        {error ? (
          <Alert
            severity="error"
            action={
              <Button
                onClick={() => {
                  setError(false);
                  void defaults.refetch();
                }}
              >
                {t("retry")}
              </Button>
            }
          >
            {t("autoCreateFailed")}
          </Alert>
        ) : null}
        {defaults.isError ? (
          <AutoRecipeDefaultsLoadError
            error={defaults.error}
            searchType={searchType}
            refresh={defaults.refetch}
          />
        ) : null}
        <RadioGroup
          value={choice}
          onChange={(event) => {
            setChoice(event.target.value as MonthChoice);
          }}
        >
          <FormControlLabel
            value="this"
            control={<Radio />}
            label={`${t("thisMonth")} (${thisMonthName})`}
            disabled={isLoading}
          />
          <FormControlLabel
            value="next"
            control={<Radio />}
            label={`${t("nextMonth")} (${nextMonthName})`}
            disabled={isLoading}
          />
        </RadioGroup>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={close} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isLoading || defaults.isFetching}
          disabled={!defaults.currentData || defaults.isError || error}
          onClick={() => {
            if (!defaults.currentData) return;
            const target = choice === "this" ? now : now.add(1, "month");
            const parameters = defaults.currentData.effective.parameters;
            void create({
              auto_recipe_type: searchType,
              on_timestamp: `${target.format("YYYY-MM")}-15T12:00:00.000Z`,
              default_context: defaults.currentData.context,
              parameters: {
                ...parameters,
                planning: {
                  ...(parameters.planning as Record<string, unknown>),
                  planning_month: target.format("YYYY-MM-01"),
                },
              },
            })
              .then(close)
              .catch(() => {
                setError(true);
                void defaults.refetch();
              });
          }}
        >
          {t("create")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
